import { useState } from "react";
import {
  Drawer,
  Button,
  Table,
  message,
  Spin,
  Alert,
  Space,
  Form,
  Input,
  Typography,
} from "antd";
import {
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
} from "@ant-design/icons";
import {
  drawerTitle,
  drawerFooter,
  DRAWER_FORM_WIDTH,
} from "../../../../components";
import {
  useConfigNamesQuery,
  useCreateConfigNameMutation,
  useUpdateConfigNameMutation,
  useDeleteConfigNameMutation,
} from "../../../../Services";

const { Text } = Typography;

interface ConfigNameItem {
  id: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export const ConfigNameManageDrawer: React.FC<Props> = ({ open, onClose }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingItem, setEditingItem] = useState<ConfigNameItem | null>(null);
  const [form] = Form.useForm();

  const {
    data: configNames = [],
    isLoading,
    isError,
    isFetching,
    error,
    refetch,
  } = useConfigNamesQuery();

  const createMutation = useCreateConfigNameMutation();
  const updateMutation = useUpdateConfigNameMutation();
  const deleteMutation = useDeleteConfigNameMutation();

  const mutationPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;
    
  const loading = isFetching || mutationPending;

  const handleCreate = () => {
    setEditingItem(null);
    setIsEditing(false);
    setIsCreating(true);
    form.resetFields();
  };

  const handleEdit = (item: ConfigNameItem) => {
    setIsCreating(false);
    setIsEditing(true);
    setEditingItem(item);
    form.setFieldsValue({ name: item.name, description: item.description ?? "" });
  };

  const handleCancelForm = () => {
    setIsCreating(false);
    setIsEditing(false);
    setEditingItem(null);
    form.resetFields();
  };

  const handleSave = async (values: any) => {
    try {
      if (isCreating) {
        const res = await createMutation.mutateAsync({
          name: values.name,
          default_value: values.default_value ?? "",
        });
        if (res?.success) {
          message.success(
            res.message || "Config name created and assigned to all tenants.",
          );
          handleCancelForm();
        } else {
          throw new Error(res?.message || "Failed to create config name.");
        }
      } else if (isEditing && editingItem) {
        const res = await updateMutation.mutateAsync({
          id: editingItem.id,
          name: values.name,
          description: values.description ?? "",
        });
        if (res?.success) {
          message.success(res.message || "Config name updated successfully.");
          handleCancelForm();
        } else {
          throw new Error(res?.message || "Failed to update config name.");
        }
      }
    } catch (err: any) {
      message.error(err.message || "Error saving config name.");
    }
  };

  const columns = [
    {
      title: "Config Name",
      dataIndex: "name",
      width:150,
      key: "name",
      render: (text: string) => (
        <Text code className="text-xs font-semibold">
          {text}
        </Text>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 200,
      render: (text: string) => (
        <span className="text-xs text-gray-600">
          {text || <span className="text-gray-300 italic">No description</span>}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 50,
      render: (_: any, item: ConfigNameItem) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined className="text-blue-500" />}
            size="small"
            onClick={() => handleEdit(item)}
          />
        </Space>
      ),
    },
  ];

  const errorMsg = isError
    ? (error as any)?.message || "Failed to load config names."
    : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={DRAWER_FORM_WIDTH}
      title={drawerTitle("Config Names", "Manage available configuration keys")}
      destroyOnClose
      footer={drawerFooter({ actions: [{ label: "Close", onClick: onClose }] })}
    >
      <div className="flex flex-col gap-4 h-full">
        {errorMsg && (
          <Alert
            message="Connection Error"
            description={
              <div className="flex flex-col gap-2">
                <p className="text-xs text-red-800">{errorMsg}</p>
                <Button
                  size="small"
                  type="primary"
                  danger
                  icon={<ReloadOutlined />}
                  onClick={() => refetch()}
                  className="w-fit text-xs"
                >
                  Retry
                </Button>
              </div>
            }
            type="error"
            showIcon
          />
        )}

        {!isCreating && !isEditing && !errorMsg && (
          <div className="flex justify-between items-center bg-gray-50 p-2.5 rounded-lg border">
            <Text className="text-xs text-gray-500">
              Config names are shared across all tenants. Adding one creates a
              default entry for every active tenant.
            </Text>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
              size="small"
              className="!bg-black !border-black hover:!bg-gray-800 text-xs"
            >
              Add Config Name
            </Button>
          </div>
        )}

        {(isCreating || isEditing) && (
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex justify-between items-center mb-3">
              <span className="text-xs font-bold text-slate-800">
                {isCreating ? "New Config Name" : "Edit Config Name"}
              </span>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
              size="middle"
            >
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: "Name is required." }]}
              >
                <Input
                  placeholder="e.g. company_name"
                  className="text-xs"
                  disabled={isEditing}
                />
              </Form.Item>

              {isCreating && (
                <Form.Item name="default_value" label="Default Value">
                  <Input placeholder="e.g. Acme Corp" className="text-xs" />
                </Form.Item>
              )}

              <Form.Item name="description" label="Description">
                <Input.TextArea
                  placeholder="What does this config control?"
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  className="text-xs"
                />
              </Form.Item>

              <Form.Item className="mb-0">
                <Space className="w-full justify-end">
                  <Button size="small" onClick={handleCancelForm}>
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={
                      createMutation.isPending || updateMutation.isPending
                    }
                    size="small"
                    className="!bg-black !border-black hover:!bg-gray-800"
                  >
                    Save
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}

        <div className="flex-grow overflow-auto relative min-h-[200px]">
          {isLoading && (
            <div className="flex items-center justify-center h-40">
              <Spin tip="Loading config names..." size="large" />
            </div>
          )}

          {configNames.length > 0 && (
            <Table
              dataSource={configNames}
              columns={columns}
              rowKey="id"
              size="small"
              pagination={false}
              className="border rounded-lg"
              locale={{ emptyText: "No config names found." }}
            />
          )}

          {configNames.length === 0 &&
            !isLoading &&
            !isCreating &&
            !isEditing && (
              <div className="text-center text-gray-400 text-sm py-10">
                No config names yet. Click "Add Config Name" to create one.
              </div>
            )}
        </div>
      </div>
    </Drawer>
  );
};
